import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import moment from "moment";
import TopBar from "../../components/TopBar";
import { SessionContext } from "../../components/SessionContext";
import { 
  IoCopyOutline, 
  IoCheckmarkOutline,
  IoLayersOutline, 
  IoReceiptOutline, 
  IoLocationOutline, 
  IoMailOutline, 
  IoChevronBackOutline,
  IoLogoWhatsapp
} from "react-icons/io5";
import { LiaShippingFastSolid } from "react-icons/lia";
import { toast } from "react-toastify";
import { PacmanLoader, BarLoader } from "react-spinners";
import Clock from "../../components/clock";
import { sendWhatsAppNotification } from "../../services/whatsappService";
let whatsappWindowRef = null;

const OrderDetail = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [isFetchingWaybill, setIsFetchingWaybill] = useState(false);
  const [isFetchingLabel, setIsFetchingLabel] = useState(false);
  const [trackingIdsInput, setTrackingIdsInput] = useState({});
  const [copiedAddress, setCopiedAddress] = useState(false);

  const getDeliveryAddressText = () => {
    if (!order) return "";
    if (typeof order.address === "string") return order.address;
    if (!order.address || typeof order.address !== "object") return "";

    const addr = order.address;
    const lines = [];
    if (addr.name) lines.push(addr.name);
    if (addr.phone || order.phone) lines.push(addr.phone || order.phone);
    if (addr.address) lines.push(addr.address);
    if (addr.place) lines.push(addr.place);

    const loc = [addr.city, addr.state].filter(Boolean).join(", ");
    const pincode = addr.zip ? ` - ${addr.zip}` : "";
    if (loc || pincode) {
      lines.push(`${loc}${pincode}`);
    }

    if (user?.postoffice_name) {
      lines.push(`Post Office: ${user.postoffice_name}`);
    }

    return lines.join("\n");
  };

  const fallbackCopyText = (text, onSuccess) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        if (onSuccess) onSuccess();
      } else {
        toast.error("Unable to copy to clipboard");
      }
    } catch (err) {
      console.error("Fallback copy error:", err);
      toast.error("Unable to copy to clipboard");
    }
  };

  const handleCopyAddress = (e) => {
    if (e) e.stopPropagation();
    const addressText = getDeliveryAddressText();
    if (!addressText) {
      toast.error("No delivery address available to copy");
      return;
    }

    const onCopySuccess = () => {
      setCopiedAddress(true);
      toast.success("Delivery address copied to clipboard!");
      setTimeout(() => setCopiedAddress(false), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(addressText)
        .then(onCopySuccess)
        .catch((err) => {
          console.error("Clipboard write error:", err);
          fallbackCopyText(addressText, onCopySuccess);
        });
    } else {
      fallbackCopyText(addressText, onCopySuccess);
    }
  };

  const { session } = useContext(SessionContext);

  useEffect(() => {
    const fetchOrder = async () => {
      let query = supabase.from("orders").select("*");

      const isNumeric = /^\d+$/.test(order_id);
      if (isNumeric) {
        query = query.eq("order_id_seq", parseInt(order_id, 10));
      } else {
        query = query.eq("order_id", order_id);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching order: ", error);
      } else if (data && data.length > 0) {
        setOrder(data[0]);
      }
    };

    fetchOrder();
  }, [order_id]);

  const [cod_charge, setCodCharge] = useState(0);

  useEffect(() => {
    if (order) {
      const newCodCharge = Object.values(order.items).reduce((total, item) => {
        let codPrice = 0;
        if (item.codPriceMap && item.size && item.codPriceMap[item.size]) {
          codPrice = item.codPriceMap[item.size]
            ? item.codPriceMap[item.size]
            : 0;
        } else {
          codPrice = item.cod_price ? item.cod_price : 0;
        }
        return total + codPrice;
      }, 0);
      setCodCharge(newCodCharge);
    }
  }, [order]);

  useEffect(() => {
    const fetchUser = async () => {
      if (order && order.user_id) {
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", order.user_id);

        if (error) console.error("Error fetching user: ", error);
        else setUser(data[0]);
      }
    };

    fetchUser();
  }, [order]);

  useEffect(() => {
    if (order) {
      let parsedIds = {};
      if (typeof order.tracking_id === "object" && order.tracking_id !== null) {
        parsedIds = { ...order.tracking_id };
      } else if (
        typeof order.tracking_id === "string" &&
        order.tracking_id.trim() !== ""
      ) {
        try {
          parsedIds = JSON.parse(order.tracking_id);
          if (typeof parsedIds !== "object" || parsedIds === null) {
            parsedIds = { legacy: order.tracking_id };
          }
        } catch (e) {
          parsedIds = { legacy: order.tracking_id };
        }
      }
      setTrackingIdsInput(parsedIds);
    }
  }, [order]);

  const cancelOrder = async (order_id, keepStock = false, block = false) => {
    try {
      // Add keepStock as a query param if true (for block, it keeps stock)
      const url = `https://ecvdsyezeauzidjqmpyc.supabase.co/functions/v1/cancel${(keepStock || block) ? "?keepStock=true" : ""
        }`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          order_id: order_id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to cancel the order");
      }

      // Direct database update to tag this appropriately
      const targetStatus = block ? "cancelled_blocked" : keepStock ? "cancelled_keep_stock" : "cancelled_by_admin";
      const { error: updateDbError } = await supabase
        .from("orders")
        .update({ order_status: targetStatus })
        .eq("order_id", order_id);
      
      if (updateDbError) {
        console.error(`Error setting status to ${targetStatus} in DB:`, updateDbError);
      }

      toast.success(block ? "Order cancelled and user blocked successfully" : "Order cancelled successfully");
      setOrder((prevOrder) => ({ ...prevOrder, order_status: targetStatus }));
    } catch (error) {
      toast.error(`${error.message}`);
    }
  };

  async function fetchShipmentLabel() {
    if (!order.waybill) return;
    if (isFetchingWaybill) return;
    if (isFetchingLabel) return;
    setIsFetchingLabel(true);
    const url = `https://ecvdsyezeauzidjqmpyc.supabase.co/functions/v1/shipment-label?awb=${order.waybill}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = await response.json();

      console.log("data", data.data);

      if (response.ok && data.status === "success") {
        //window.open(data.pdf, "_blank");
        navigate("/print", { state: { packageData: data.data } });
      } else {
        toast.error(`Error: ${data.error || "Waybill Label not found"}`);
      }
    } catch (error) {
      console.error("Error fetching shipment label:", error);
      toast.error("Failed to fetch shipment label");
    }
    setIsFetchingLabel(false);
  }

  const sendShipMentRequest = async () => {
    if (isFetchingWaybill) return;
    setIsFetchingWaybill(true);
    if (order.waybill) {
      // Copy the waybill to clipboard
      navigator.clipboard.writeText(order.waybill);
      toast.success("Waybill copied to clipboard");
      setIsFetchingWaybill(false);
      return;
    }

    if (order && order.order_id && !order.waybill) {
      try {
        const response = await fetch(
          `https://ecvdsyezeauzidjqmpyc.supabase.co/functions/v1/shipment-creation?orderId=${order.order_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );

        const data = await response.json();

        if (response.ok) {
          toast.success("Shipment created successfully");
          // Update the order state with the new waybill
          setOrder((prevOrder) => ({
            ...prevOrder,
            waybill: data.waybill,
          }));
        } else {
          toast.error(`Error: ${data.error_message}`);
        }
      } catch (error) {
        console.error("Error creating shipment:", error);
        toast.error("Error creating shipment");
      }

      setIsFetchingWaybill(false);
    }
  };

  const updateOrderStatus = async (status) => {
    if (!order || !order.order_id) return;

    if (status === "cancel_release_stock") {
      cancelOrder(order.order_id);
      return;
    }
    if (status === "cancelled_keep_stock") {
      cancelOrder(order.order_id, true);
      return;
    }
    if (status === "cancelled_blocked") {
      cancelOrder(order.order_id, true, true);
      return;
    }

    try {
      const wasRestocked = order.order_status === "cancelled_keep_stock" || order.order_status === "cancelled_blocked";
      const isNewStatusActive = status !== "cancelled" && status !== "cancelled_by_admin" && status !== "cancelled_keep_stock" && status !== "cancelled_blocked";

      if (wasRestocked && isNewStatusActive) {
        toast.info("Re-deducting stock inventory for reactivated order...");
        const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
        for (const item of itemsArray) {
          if (!item.id) continue;
          
          // 1. Fetch current product
          const { data: product, error: fetchError } = await supabase
            .from("products")
            .select("id, stock, stockMap")
            .eq("id", item.id)
            .maybeSingle();

          if (fetchError || !product) {
            console.error(`Product not found or error fetching for ID ${item.id}:`, fetchError);
            continue;
          }

          // 2. Decrement stock based on size or general stock
          if (item.size && product.stockMap) {
            const sizeKey = String(item.size);
            const currentStockMap = { ...product.stockMap };
            const currentSizeStock = Number(currentStockMap[sizeKey]) || 0;
            currentStockMap[sizeKey] = Math.max(0, currentSizeStock - (Number(item.quantity) || 1));

            const { error: updateError } = await supabase
              .from("products")
              .update({ stockMap: currentStockMap })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating stockMap for product ${product.id}:`, updateError);
            }
          } else {
            const currentStock = Number(product.stock) || 0;
            const newStock = Math.max(0, currentStock - (Number(item.quantity) || 1));

            const { error: updateError } = await supabase
              .from("products")
              .update({ stock: newStock })
              .eq("id", product.id);
            if (updateError) {
              console.error(`Error updating general stock for product ${product.id}:`, updateError);
            }
          }
        }
      }

      // If the new status is NOT unfulfilled (waiting), clear the hold status
      const updatePayload = { order_status: status };
      if (status !== "waiting") {
        updatePayload.hold_status = null;
      }

      const { error } = await supabase
        .from("orders")
        .update(updatePayload)
        .eq("order_id", order.order_id);

      if (error) {
        console.error("Error updating order status: ", error);
        toast.error("Failed to update status: " + error.message);
      } else {
        toast.success("Order status updated successfully!");
        setOrder((prevOrder) => ({
          ...prevOrder,
          order_status: status,
          hold_status: status !== "waiting" ? null : prevOrder.hold_status
        }));
      }
    } catch (err) {
      console.error("Error updating status & stock:", err);
      toast.error("An error occurred during status update.");
    }
  };

  const updateHoldStatus = async (holdStatus) => {
    if (!order || !order.order_id) return;

    try {
      const targetHold = holdStatus === "none" ? null : holdStatus;

      const { error } = await supabase
        .from("orders")
        .update({ hold_status: targetHold })
        .eq("order_id", order.order_id);

      if (error) {
        console.error("Error updating hold status: ", error);
        toast.error("Failed to update hold status: " + error.message);
      } else {
        toast.success(`Hold status updated to ${holdStatus === "none" ? "None" : holdStatus}!`);
        setOrder((prevOrder) => ({ ...prevOrder, hold_status: targetHold }));
      }
    } catch (err) {
      console.error("Error updating hold status:", err);
      toast.error("An error occurred during hold status update.");
    }
  };

  const saveTrackingId = async (itemKey, newValue) => {
    if (!order || !order.order_id) return false;

    let currentTracking = {};
    if (typeof order.tracking_id === "object" && order.tracking_id !== null) {
      currentTracking = { ...order.tracking_id };
    } else if (
      typeof order.tracking_id === "string" &&
      order.tracking_id.trim() !== ""
    ) {
      try {
        currentTracking = JSON.parse(order.tracking_id);
        if (typeof currentTracking !== "object" || currentTracking === null) {
          currentTracking = {};
        }
      } catch (e) {
        currentTracking = { legacy: order.tracking_id };
      }
    }

    const updatedTracking = {
      ...currentTracking,
      [itemKey]: newValue,
    };

    const { data, error } = await supabase
      .from("orders")
      .update({ tracking_id: JSON.stringify(updatedTracking) })
      .eq("order_id", order.order_id)
      .select();

    if (error) {
      console.error("Error updating tracking ID: ", error);
      toast.error(`Error saving tracking ID: ${error.message || "Unknown Database Error"}`);
      return false;
    } else if (!data || data.length === 0) {
      console.warn("Update returned 0 rows. Please verify order exists and you have permission.", { order_id: order.order_id });
      toast.warning("No database rows were updated. Check if order exists and permissions are set.");
      return false;
    } else {
      toast.success("Tracking ID updated successfully!");
      setOrder((prevOrder) => ({ ...prevOrder, tracking_id: updatedTracking }));
      return true;
    }
  };

  const sendWhatsAppForItem = async (itemKey, trackingVal) => {
    if (!order) return false;
    const currentTrackingId = trackingVal || (typeof order.tracking_id === "object" ? order.tracking_id[itemKey] : null);

    if (!currentTrackingId || !String(currentTrackingId).trim()) {
      toast.warning("Please enter a Tracking ID before sending to WhatsApp!");
      return false;
    }

    const trimmedTrackingId = String(currentTrackingId).trim();
    const wpNumber = order.address?.whatsapp || order.address?.phone || order.whatsapp || order.phone;
    if (!wpNumber) {
      toast.error("No phone or WhatsApp number found for this order!");
      return false;
    }

    const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
    const isPerfumes = (order.template_type && order.template_type.toLowerCase().includes("perfume")) ||
      (order.store_name && order.store_name.toLowerCase().includes("perfume")) ||
      itemsArray.some(item =>
        (item?.name && item.name.toLowerCase().includes("perfume")) ||
        (item?.category && item.category.toLowerCase().includes("perfume"))
      );

    const templateName = isPerfumes ? "taqwa_perfumes" : "taqwa_fashion_store";
    const customerName = order.address?.name || user?.raw_user_meta_data?.full_name || order.name || "Customer";

    const res = await sendWhatsAppNotification({
      to: wpNumber,
      customerName,
      trackingId: trimmedTrackingId,
      templateName,
    });

    return res.success;
  };

  const handleSaveAndWhatsApp = async (itemKey, trackingVal) => {
    if (!trackingVal) {
      toast.warning("Please enter a Tracking ID before saving and sending to WhatsApp!");
      return;
    }

    const saved = await saveTrackingId(itemKey, trackingVal);
    if (!saved) {
      return;
    }

    await sendWhatsAppForItem(itemKey, trackingVal);

    if (order && order.order_status === "waiting") {
      await updateOrderStatus("shipped");
    }
  };


  const sendWhatsAppUpdate = async (e) => {
    if (e) e.preventDefault();
    if (!order) return;
    const wpNumber = order.address?.whatsapp || order.address?.phone || order.whatsapp || order.phone;
    if (!wpNumber) {
      toast.error("No phone or WhatsApp number found for this order!");
      return;
    }

    // Extract all unique tracking IDs
    let trackingStr = "";
    if (order.tracking_id) {
      let parsedIds = {};
      if (typeof order.tracking_id === "object") {
        parsedIds = order.tracking_id;
      } else {
        try {
          parsedIds = JSON.parse(order.tracking_id);
        } catch (e) {
          parsedIds = { legacy: order.tracking_id };
        }
      }
      const uniqueIds = [...new Set(Object.values(parsedIds).filter(id => id && String(id).trim() !== ""))];
      if (uniqueIds.length > 0) {
        trackingStr = uniqueIds.join(', ');
      }
    }

    const trackingId = trackingStr || "EL789221150IN";

    const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
    const isPerfumes = (order.template_type && order.template_type.toLowerCase().includes("perfume")) ||
      (order.store_name && order.store_name.toLowerCase().includes("perfume")) ||
      itemsArray.some(item =>
        (item?.name && item.name.toLowerCase().includes("perfume")) ||
        (item?.category && item.category.toLowerCase().includes("perfume"))
      );

    const templateName = isPerfumes ? "taqwa_perfumes" : "taqwa_fashion_store";
    const customerName = order.address?.name || user?.raw_user_meta_data?.full_name || order.name || "Customer";

    await sendWhatsAppNotification({
      to: wpNumber,
      customerName,
      trackingId,
      templateName,
    });
  };

  if (!order || !user)
    return (
      <div>
        <Clock />
      </div>
    );
  const printDetails = () => {
    let printWindow = window.open("", "_blank");

    const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});
    const isCod = order.payment_method === "COD";
    const dateStr = moment(order.created_at).format("DD-MM-YYYY");
    const timeStr = moment(order.created_at).format("HH:mm");

    const productsHtml = itemsArray.map(item => {
      const sizeStr = item?.size ? ` [${item.size}]` : '';
      const price = Number(item.priceMap ? item.priceMap[item.size] : item.price) || 0;
      const qty = Number(item.quantity) || 1;
      if (isCod) {
        return `
          <tr>
            <td>${item?.name || 'Product'}${sizeStr} (${qty})</td>
            <td style="text-align: center;">${price}</td>
            <td style="text-align: center; border-right: none;">${price * qty}</td>
          </tr>
        `;
      } else {
        return `
          <tr>
            <td>${item?.name || 'Product'}${sizeStr} (${qty})</td>
            <td style="text-align: center; border-right: none;">${price * qty}</td>
          </tr>
        `;
      }
    }).join('');

    if (isCod) {
      printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${order.order_id_seq || order.order_id}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&family=Roboto:wght@400;700;900&display=swap" rel="stylesheet">

<style>
  @page {
    size: 4in 6in;
    margin: 0.1in;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    width: 3.8in;
    height: 5.8in;
  }

  body {
    font-family: 'Roboto Condensed', 'Roboto', sans-serif;
    font-size: 10pt;
    color: #000;
    background: #fff;
    text-transform: uppercase;
  }

  .label-wrapper {
    width: 100%;
    height: 100%;
    border: 2px solid #000;
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    width: 100%;
    border-bottom: 1.5px solid #000;
    overflow: hidden;
  }

  .row:last-child {
    border-bottom: none;
  }

  .row-header      { flex: 1.0; }
  .row-pin         { flex: 0.5; }
  .row-shipto      { flex: 5.2; }
  .row-seller-date { flex: 1.8; } 
  .row-products    { flex: 3.2; display: block; }
  .row-orderid     { flex: 0.6; }
  .row-footer      { flex: 0.5; }

  .store-name {
    font-size: 17pt;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }

  .header-full {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
  }

  .pin-left {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }

  .route-right {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .shipto-cell {
    width: 62%;
    padding: 4px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }

  .shipto-addr-heavy {
    font-size: 10.5pt;
    font-weight: 900;
    line-height: 1.3;
    margin-top: 2px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .cod-cell {
    width: 38%;
    padding: 7px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #fff;
  }

  .cod-amount-title {
    font-weight: 900;
    font-size: 10pt; 
    line-height: 1.1;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .shipto-name {
    font-size: 12pt;
    font-weight: 900;
  }

  .seller-cell {
    width: 65%;
    padding: 7px 9px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .seller-addr-bold {
    font-size: 8.5pt;
    font-weight: 700; 
    line-height: 1.4;
  }

  .product-table {
    width: 100%;
    border-collapse: collapse;
  }

  .product-table th, .product-table td {
    padding: 5px 7px;
    border-right: 1.5px solid #000;
    font-weight: 900;
  }

  .product-table th {
    font-size: 8pt;
    border-bottom: 1.5px solid #000;
    background: #ebebeb;
    text-align: left;
  }

  .product-table td {
    font-size: 9pt;
    border-bottom: 1px solid #ccc;
  }

  .order-id-text {
    font-size: 12pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
  }

  .footer-text {
    font-size: 14pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
  }
</style>
</head>

<body>

<div class="label-wrapper">

  <!-- 1. STORE HEADER -->
  <div class="row row-header">
    <div class="header-full">
      <div class="store-name">TAQWA FASHION STORE</div>
    </div>
  </div>

  <!-- 2. PINCODE / ROUTE -->
  <div class="row row-pin">
    <div class="pin-left">PINCODE: ${order.address.zip}</div>
    <div class="route-right">COD / SPEEDPOST</div>
  </div>

  <!-- 3. SHIP TO + COD BOX -->
  <div class="row row-shipto">
    <div class="shipto-cell">
      <div>
        <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SHIP TO:</div>
        <div class="shipto-name">${order.address.name}</div>
        <div class="shipto-addr-heavy">
          ${order.address.address}<br>
          ${order.address.place ? order.address.place + '<br>' : ''}
          ${user && user.postoffice_name ? 'Postoffice Name: ' + user.postoffice_name + '<br>' : ''}
          ${order.address.city}, ${order.address.state}<br>
          PIN: ${order.address.zip}
        </div>
      </div>
      <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
        PH: ${order.address.phone || order.phone || ""}
      </div>
    </div>

    <div class="cod-cell">
      <div class="cod-amount-title">COD AMOUNT</div>
      <div style="font-size: 20pt; font-weight: 900; line-height: 1;">
        ${order.amount}
      </div>
      
      <div style="border: 1.5px solid #000; padding: 2px 4px; margin-top: 6px; width: 100%;">
        <div style="font-size: 6.5pt; font-weight: 900; margin-bottom: -2px;">E-BILLER ID</div>
        <div style="font-size: 13pt; font-weight: 900; letter-spacing: 1px;">55938</div>
      </div>
    </div>
  </div>

  <!-- 4. SELLER + ORDER DATE -->
  <div class="row row-seller-date">
    <div class="seller-cell">
      <div style="font-size: 8pt; font-weight: 700; margin-bottom: 2px;">SELLER:</div>
      <div class="shipto-name" style="font-size: 9pt;">TAQWA FASHION STORE</div>
      <div class="seller-addr-bold">
        PH: 7012556844<br>
        KANJIRAMATTOM, KERALA<br>
        INDIA 682315
      </div>
    </div>

    <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
      <div style="font-size: 8pt; font-weight: 700;">ORDER DATE</div>
      <div style="font-size: 9pt; font-weight: 900;">
        ${dateStr}<br>
        ${timeStr}
      </div>
    </div>
  </div>

  <!-- 5. PRODUCT TABLE -->
  <div class="row row-products">
    <table class="product-table">
      <thead>
        <tr>
          <th style="width: 58%;">PRODUCT (QTY)</th>
          <th style="width: 21%; text-align: center;">PRICE</th>
          <th style="width: 21%; text-align: center; border-right: none;">TOTAL</th>
        </tr>
      </thead>

      <tbody>
        ${productsHtml}

        <tr style="background: #ebebeb;">
          <td style="border-top: 1.5px solid #000;">TOTAL</td>
          <td colspan="2"
              style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 10pt;">
            INR ${order.amount}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- 6. ORDER ID ROW -->
  <div class="row row-orderid"
       style="display:flex; align-items:center; justify-content:center;">
    <div class="order-id-text">
      ORDER ID ${order.order_id_seq || order.order_id}
    </div>
  </div>

  <!-- 7. FOOTER ROW -->
  <div class="row row-footer"
       style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
    <div class="footer-text">
      THANK YOU FOR SHOPPING
    </div>
  </div>

</div>

</body>
</html>`);
    } else {
      printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${order.order_id_seq || order.order_id}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700;900&family=Roboto:wght@400;700;900&display=swap" rel="stylesheet">

<style>
  @page {
    size: 4in 6in;
    margin: 0.1in;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    width: 3.8in;
    height: 5.8in;
  }

  body {
    font-family: 'Roboto Condensed', 'Roboto', sans-serif;
    font-size: 10pt;
    color: #000;
    background: #fff;
    text-transform: uppercase;
  }

  .label-wrapper {
    width: 100%;
    height: 100%;
    border: 2px solid #000;
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    width: 100%;
    border-bottom: 1.5px solid #000;
    overflow: hidden;
  }

  .row:last-child {
    border-bottom: none;
  }

  .row-header      { flex: 1.0; }
  .row-pin         { flex: 0.5; }
  .row-shipto      { flex: 5.4; }
  .row-seller-date { flex: 1.8; } 
  .row-products    { flex: 2.7; display: block; }
  .row-orderid     { flex: 0.6; } 
  .row-footer      { flex: 0.6; }

  .store-name {
    font-size: 17pt;
    font-weight: 900;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }

  .header-full {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
  }

  .pin-left {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }

  .type-right {
    width: 50%;
    padding: 4px 8px;
    font-size: 10pt;
    font-weight: 900;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .shipto-cell {
    width: 72%; 
    padding: 4px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
  }

  .shipto-addr-heavy {
    font-size: 10.5pt;
    font-weight: 900;
    line-height: 1.3;
    margin-top: 2px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .prepaid-cell {
    width: 28%;
    padding: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: #f2f2f2;
  }

  .ebiller-box-inner {
    border: 1.5px solid #000;
    padding: 5px 2px;
    width: 100%;
  }

  .ebiller-id {
    font-size: 13pt; 
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.1;
  }

  .paid-text-ultra {
    font-weight: 900;
    font-size: 28pt; 
    letter-spacing: 2px;
    line-height: 1;
    margin-top: 8px;
  }

  .order-subtext {
    font-weight: 400;
    font-size: 9.5pt;
    letter-spacing: 1.5px;
    margin-top: 4px;
  }

  .seller-cell {
    width: 65%;
    padding: 7px 9px;
    border-right: 1.5px solid #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .seller-addr-bold {
    font-size: 9pt;
    font-weight: 900; 
    line-height: 1.4;
  }

  .product-table {
    width: 100%;
    border-collapse: collapse;
  }

  .product-table th, .product-table td {
    border-right: 1.5px solid #000;
    padding: 5px 7px;
    font-weight: 900;
  }

  .product-table th {
    font-size: 8pt;
    background: #ebebeb;
    border-bottom: 1.5px solid #000;
    text-align: left;
  }

  .product-table td {
    font-size: 9pt;
    border-bottom: 1px solid #ccc;
  }

  .order-id-box {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    padding: 4px;
  }

  .order-id-text {
    font-size: 12pt;
    font-weight: 900;
    letter-spacing: 1px;
  }

  .footer-text {
    font-size: 16pt;
    font-weight: 900;
    letter-spacing: 1px;
    text-align: center;
    width: 100%;
    line-height: 1;
  }
</style>
</head>

<body>

<div class="label-wrapper">

  <div class="row row-header">
    <div class="header-full">
      <div class="store-name">TAQWA FASHION STORE</div>
    </div>
  </div>

  <div class="row row-pin">
    <div class="pin-left">PINCODE: ${order.address.zip}</div>
    <div class="type-right">PREPAID / SPEEDPOST</div>
  </div>

  <div class="row row-shipto">
    <div class="shipto-cell">
      <div>
        <div style="font-size: 9pt; font-weight: 900; margin-bottom: 2px;">SHIP TO:</div>
        <div style="font-size: 12pt; font-weight: 900;">${order.address.name}</div>
        <div class="shipto-addr-heavy">
          ${order.address.address}<br>
          ${order.address.place ? order.address.place + '<br>' : ''}
          ${user && user.postoffice_name ? 'Postoffice Name: ' + user.postoffice_name + '<br>' : ''}
          ${order.address.city}, ${order.address.state}<br>
          PIN: ${order.address.zip}
        </div>
      </div>
      <div style="font-size: 13pt; font-weight: 900; margin-top: 4px; line-height: 1.2;">
        PH: ${order.address.phone || order.phone || ""}
      </div>
    </div>

    <div class="prepaid-cell">
      <div class="ebiller-box-inner">
        <div style="font-size: 8pt; font-weight: 900;">E-BILLER ID</div>
        <div class="ebiller-id">55938</div>
      </div>

      <div class="paid-text-ultra">PAID</div>
      <div class="order-subtext">ORDER</div>
    </div>
  </div>

  <div class="row row-seller-date">
    <div class="seller-cell">
      <div style="font-size: 9pt; font-weight: 900; margin-bottom: 2px;">
        SELLER:
      </div>

      <div class="seller-addr-bold">
        TAQWA FASHION STORE<br>
        PH: 7012556844<br>
        KANJIRAMATTOM P O ERNAKULAM<br>
        KERALA 682315
      </div>
    </div>

    <div style="width: 35%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
      <div style="font-size: 8pt; font-weight: 900;">ORDER DATE</div>

      <div style="font-size: 10pt; font-weight: 900;">
        ${dateStr}<br>
        ${timeStr}
      </div>
    </div>
  </div>

  <div class="row row-products">
    <table class="product-table">
      <thead>
        <tr>
          <th style="width: 65%;">ITEM (QTY)</th>
          <th style="width: 35%; text-align: center; border-right: none;">
            PRICE
          </th>
        </tr>
      </thead>

      <tbody>
        ${productsHtml}

        <tr style="background: #ebebeb;">
          <td style="border-top: 1.5px solid #000; font-size: 11pt;">
            TOTAL
          </td>

          <td style="text-align: center; border-top: 1.5px solid #000; border-right: none; font-size: 11pt;">
            ${order.amount}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="row row-orderid">
    <div class="order-id-box">
      <div class="order-id-text">
        ORDER ID ${order.order_id_seq || order.order_id}
      </div>
    </div>
  </div>

  <div class="row row-footer"
       style="display:flex; align-items:center; justify-content:center; border-bottom: none;">
    <div class="footer-text">
      THANK YOU FOR SHOPPING
    </div>
  </div>

</div>

</body>
</html>`);
    }
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
  const itemsArray = Array.isArray(order.items) ? order.items : Object.values(order.items || {});

  const getStatusBadgeStyles = (status) => {
    switch (status) {
      case "waiting":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "shipped":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "delivered":
        return "bg-green-50 text-green-700 border-green-200";
      case "cancelled_by_admin":
      case "cancelled_keep_stock":
      case "cancelled_blocked":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "waiting":
        return "Unfulfilled";
      case "shipped":
        return "Shipped";
      case "delivered":
        return "Delivered";
      case "cancelled_by_admin":
        return "Cancelled & Released Stock";
      case "cancelled_keep_stock":
        return "Cancelled Keep Stock";
      case "cancelled_blocked":
        return "Cancelled & Blocked";
      default:
        return status || "Unknown";
    }
  };

  return (
    <>
      <TopBar avatarInfo={session?.user.user_metadata} />
      <div className="page min-h-screen bg-slate-50 text-slate-800 font-sans pb-24">
        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b border-slate-200 pb-6 mb-6">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <button 
                  onClick={() => navigate("/orders")} 
                  className="flex items-center gap-1 hover:text-blue-600 transition-colors font-medium"
                >
                  <IoChevronBackOutline size={14} />
                  <span>Orders</span>
                </button>
                <span>/</span>
                <span className="text-slate-700 font-medium">Order Detail</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
                Order <span className="text-blue-600">#{order.order_id_seq || order.order_id}</span>
              </h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <button
                className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 text-sm"
                onClick={() => printDetails()}
              >
                Print Taqwa Invoice
              </button>
              
              <button
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 text-sm"
                onClick={sendShipMentRequest}
              >
                {isFetchingWaybill ? (
                  <PacmanLoader color="white" size={10} />
                ) : order && order.waybill ? (
                  <>
                    <span>Waybill: {order.waybill}</span>
                    <IoCopyOutline className="text-base" />
                  </>
                ) : (
                  <>
                    <span>Create Shipment</span>
                    <LiaShippingFastSolid className="text-base" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Items */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between bg-slate-50/50">
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <IoLayersOutline className="text-slate-500 text-xl" />
                    Items ({itemsArray.length})
                  </h2>
                </div>
                
                <div className="p-6 space-y-6">
                  {itemsArray.map((item) => (
                    <div key={`${item.id}_${item.size || "default"}`} className="flex flex-col sm:flex-row gap-6 p-4 rounded-xl border border-slate-100 bg-slate-50/30 hover:bg-slate-50/60 transition-all duration-200">
                      {/* Product Image */}
                      <div className="w-full sm:w-40 h-40 flex-shrink-0 bg-white border border-slate-200 rounded-lg overflow-hidden group">
                        <img
                          src={item?.images?.[0] || ""}
                          alt={item?.name || "Product"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      
                      {/* Product Specs & Tracking */}
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between gap-4 mb-1">
                            <h3 className="text-lg font-bold text-slate-900 hover:text-blue-600 transition-colors">{item.name}</h3>
                            <span className="text-lg font-extrabold text-slate-900">₹{item.priceMap ? item.priceMap[item.size] : item.price}</span>
                          </div>
                          
                          <p className="text-sm text-slate-500 mb-3">Category: {item.category}</p>
                          
                          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700 mb-4 bg-white p-3 rounded-lg border border-slate-100">
                            <div>
                              <span className="text-slate-400 font-medium">Quantity:</span> <span className="font-bold text-slate-800">{item.quantity}</span>
                            </div>
                            {item.size !== 0 && (
                              <div>
                                <span className="text-slate-400 font-medium">Size:</span> <span className="font-bold text-slate-800">{item.size}</span>
                              </div>
                            )}
                            {(item.color || item.brand) && (
                              <div>
                                <span className="text-slate-400 font-medium">Color/Brand:</span> <span className="font-bold text-slate-800">{item.color || item.brand}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Tracking Section */}
                        <div className="border-t border-slate-100/80 pt-4 mt-auto">
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Item Tracking ID
                          </label>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative flex-1">
                              <input
                                type="text"
                                className="w-full border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm bg-white"
                                placeholder="Enter tracking ID for this item"
                                value={trackingIdsInput[`${item.id}_${item.size || "default"}`] || ""}
                                onChange={(e) => {
                                  const key = `${item.id}_${item.size || "default"}`;
                                  setTrackingIdsInput((prev) => ({
                                    ...prev,
                                    [key]: e.target.value,
                                  }));
                                }}
                              />
                            </div>
                            <button
                              onClick={() =>
                                handleSaveAndWhatsApp(
                                  `${item.id}_${item.size || "default"}`,
                                  trackingIdsInput[`${item.id}_${item.size || "default"}`]
                                )
                              }
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg text-sm shadow-sm transition-colors flex items-center justify-center gap-2"
                              title="Save Tracking ID and send info via WhatsApp"
                            >
                              <IoLogoWhatsapp className="text-base" />
                              <span>Save & Send</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Order Details & Sidebar */}
            <div className="space-y-6">
              {/* Order Information Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-2 bg-slate-50/50">
                  <IoReceiptOutline className="text-slate-500 text-lg" />
                  <h2 className="text-base font-bold text-slate-900">Order Information</h2>
                </div>
                
                <div className="p-6 space-y-4">
                  {/* Status Badge & Dropdown */}
                  <div className="pb-4 border-b border-slate-100">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Order Status</label>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold">Current Status:</span>
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${getStatusBadgeStyles(order.order_status)}`}>
                        {getStatusLabel(order.order_status)}
                      </span>
                    </div>
                    
                    <select
                      value={order.order_status || "waiting"}
                      onChange={(e) => updateOrderStatus(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer"
                    >
                      <option value="waiting">Unfulfilled</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancel_release_stock">Cancel Release Stock</option>
                      <option value="cancelled_keep_stock">Cancel Keep Stock</option>
                      <option value="cancelled_blocked">Cancel and Block</option>
                    </select>
                  </div>

                  {/* Hold Status Dropdown (Shown ONLY for Unfulfilled orders) */}
                  {order.order_status === "waiting" && (
                    <div className="pb-4 border-b border-slate-100 bg-amber-50/40 p-4 rounded-lg border border-amber-100/50">
                      <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                        Hold Status (Required for Unfulfilled)
                      </label>
                      <select
                        value={order.hold_status || "none"}
                        onChange={(e) => updateHoldStatus(e.target.value)}
                        className="w-full bg-white border border-amber-200 text-amber-900 rounded-lg py-2 px-3 text-sm font-bold shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
                      >
                        <option value="none">No Hold (Clear)</option>
                        <option value="HOLD-1">HOLD-1</option>
                        <option value="HOLD-2">HOLD-2</option>
                        <option value="HOLD-3">HOLD-3</option>
                      </select>
                      {order.hold_status && (
                        <div className="mt-2 text-xs font-bold text-amber-700 bg-amber-100/50 py-1.5 px-2.5 rounded flex items-center justify-between">
                          <span>Active Hold: {order.hold_status}</span>
                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Order Details List */}
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500">Payment Method</span>
                      <span className={`font-bold ${order.payment_method === "COD" ? "text-red-500" : "text-green-600"}`}>
                        {order.payment_method === "COD" ? "CASH ON DELIVERY" : "ONLINE"}
                      </span>
                    </div>

                    {order.payment_id && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-slate-500">Payment ID</span>
                        <span className="font-mono text-xs font-semibold text-slate-800 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">{order.payment_id}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500">Date & Time</span>
                      <span className="font-semibold text-slate-800">
                        {moment(order.created_at).format("MMM DD, YYYY - h:mm A")}
                      </span>
                    </div>

                    {user?.postoffice_name && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-slate-500">Postoffice</span>
                        <span className="font-bold text-slate-800 uppercase">{user.postoffice_name}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                      <span className="text-base font-bold text-slate-900">Total Amount</span>
                      <span className="text-lg font-extrabold text-blue-600">₹{order.amount}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Address Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between bg-slate-50/50">
                  <div className="flex items-center gap-2">
                    <IoLocationOutline className="text-slate-500 text-lg" />
                    <h2 className="text-base font-bold text-slate-900">Delivery Address</h2>
                  </div>
                  {order && order.address && (
                    <button
                      type="button"
                      onClick={handleCopyAddress}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition-all duration-150 active:scale-95 cursor-pointer select-none"
                      title="Copy delivery address to clipboard"
                    >
                      {copiedAddress ? (
                        <>
                          <IoCheckmarkOutline className="text-sm text-green-600 stroke-[2.5]" />
                          <span className="text-green-600 font-bold">Copied!</span>
                        </>
                      ) : (
                        <>
                          <IoCopyOutline className="text-sm text-slate-500" />
                          <span>Copy Address</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                <div className="p-6 space-y-4 text-sm text-slate-700 select-text selection">
                  {order && order.address ? (
                    typeof order.address === "string" ? (
                      <p className="text-slate-800 whitespace-pre-wrap select-text selection cursor-text leading-relaxed">
                        {order.address}
                      </p>
                    ) : (
                      <div className="space-y-1.5 select-text selection cursor-text">
                        <p className="font-bold text-slate-900 text-base select-text selection cursor-text">{order.address.name}</p>
                        <p className="font-semibold text-slate-800 select-text selection cursor-text">{order.address.phone}</p>
                        <p className="text-slate-600 leading-relaxed select-text selection cursor-text">{order.address.address}</p>
                        {order.address.place && <p className="text-slate-600 select-text selection cursor-text">{order.address.place}</p>}
                        <p className="font-semibold text-slate-800 select-text selection cursor-text">
                          {order.address.city}, {order.address.state} - {order.address.zip}
                        </p>
                      </div>
                    )
                  ) : (
                    <p className="text-slate-400 italic">No delivery address provided.</p>
                  )}
                  
                  <div className="pt-3 border-t border-slate-100 space-y-3">
                    <div className="flex items-center justify-between select-text selection cursor-text">
                      <span className="text-slate-500 select-text selection cursor-text">Postoffice Name</span>
                      {user && user.postoffice_name ? (
                        <span className="font-bold text-slate-800 select-text selection cursor-text">{user.postoffice_name}</span>
                      ) : (
                        <span className="text-red-500 font-extrabold text-xs bg-red-50 px-2 py-0.5 rounded border border-red-100 select-none">NOT ENTERED</span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between select-text selection">
                      <span className="text-slate-500 select-text selection cursor-text">WhatsApp Notification</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold select-text selection cursor-text">{order?.address?.whatsapp || order?.address?.phone}</span>
                        <button
                          onClick={sendWhatsAppUpdate}
                          className="p-1.5 bg-[#25d366] hover:bg-[#20ba59] text-white rounded-full transition-colors shadow-sm cursor-pointer select-none"
                          title="Send update via WhatsApp"
                        >
                          <IoLogoWhatsapp className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Email Information Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-2 bg-slate-50/50">
                  <IoMailOutline className="text-slate-500 text-lg" />
                  <h2 className="text-base font-bold text-slate-900">Customer Account</h2>
                </div>
                
                <div className="p-6 space-y-3 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">Name</span>
                    <span className="font-bold text-slate-900">{user.raw_user_meta_data?.full_name || "Guest User"}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-slate-500">Email</span>
                    <span className="font-semibold text-slate-800 break-all select-all">{user.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
            <button
              className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors shadow-sm text-sm"
              onClick={() => navigate("/orders")}
            >
              Back to Orders
            </button>

            {order && order.waybill && (
              <button
                className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-colors shadow-sm text-sm"
                onClick={fetchShipmentLabel}
              >
                {isFetchingLabel ? (
                  <BarLoader width={120} color="white" height={4} />
                ) : (
                  "Delhivery Shipping Label"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default OrderDetail;
